import { User } from '@repo/domain/user';

import { ImageGenerationModel } from '../components/AiGenerators/types';

export interface UserCreditsInfo {
  resetTime: number;
  formattedResetTime: string;
  remainingAmount: number;
  amount: number;
  extraCredits: number;
  total: number;
}

/**
 * Formats the reset time to a human-readable string
 * @param resetTime The timestamp of the reset time
 * @returns A human-readable string of the reset time
 */

export const formatResetTime = (resetTime: number): string => {
  const now = Date.now(); // Current timestamp
  const realResetTime = resetTime + 1000; // Add 1 second to the reset time, because the timestamp ends at 59m and 59s
  const remaining = realResetTime - now; // Time difference in milliseconds
  const oneDayInMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  if (remaining > oneDayInMs) {
    // More than 24 hours before the end
    return `Renewing: ${new Date(realResetTime).toLocaleDateString('default', { day: '2-digit', month: 'long', year: 'numeric' })}`; // Show day/month/year in locale format
  } else if (remaining > 0) {
    // Within the last 24 hours
    const remainingHours = Math.floor(remaining / (60 * 60 * 1000)); // Convert to hours
    return `Renewing in ${remainingHours} hour${remainingHours > 1 ? 's' : ''}`;
  } else {
    // If timestamp has already passed
    return '';
  }
};

export const getUserCreditsInfo = (user: User): UserCreditsInfo => {
  const creditInfo = user.creditInfo;

  const resetTime = creditInfo?.recurringExpiresAt
    ? new Date(creditInfo.recurringExpiresAt).getTime()
    : 0;
  const formattedResetTime = formatResetTime(resetTime);
  const remainingAmount = creditInfo?.recurringRemaining ?? 0;
  const amount = creditInfo?.recurringTotal ?? 0;
  const extraCredits = creditInfo?.extraTotal ?? 0;
  const total = remainingAmount + extraCredits;

  return {
    resetTime,
    formattedResetTime,
    remainingAmount,
    amount,
    extraCredits,
    total,
  };
};

export const userHasEnoughCredits = (
  user: User | null,
  cost?: number
): boolean => {
  if (!user) {
    return false;
  }
  if (!cost) {
    return true;
  }

  const { total } = getUserCreditsInfo(user);
  return total >= cost;
};

export enum ActionsWithCost {
  ImageGeneration = 'ImageGeneration',
  QuoteGenerator = 'QuoteGenerator',
  BackgroundReplacer = 'BackgroundReplacer',
  BackgroundRemoverWebsite = 'BackgroundRemoverWebsite',
  SvgConverterWebsite = 'SvgConverterWebsite',
  ImageUpscaler = 'ImageUpscaler',
  DesignGenerator = 'DesignGenerator',
  DesignGeneratorFreeUser = 'DesignGeneratorFreeUser',
  ImageReplacing = 'ImageReplacing',
  TextGenerator = 'TextGenerator',
  SetsGenerator = 'SetsGenerator',
  StyleSwitch = 'StyleSwitch',
  Outpainting = 'Outpainting',
}

export const creditsCostUtil = {
  [ActionsWithCost.ImageGeneration]: {
    [ImageGenerationModel.Turbo]: 1,
    [ImageGenerationModel.Advanced]: 2,
    [ImageGenerationModel.FluxPro]: 2,
    [ImageGenerationModel.Ideogram]: 2,
  },
  [ActionsWithCost.QuoteGenerator]: 1,
  [ActionsWithCost.BackgroundReplacer]: 1,
  [ActionsWithCost.SvgConverterWebsite]: 1, // Only for free users. Paid users get it for free
  [ActionsWithCost.ImageUpscaler]: 1,
  [ActionsWithCost.DesignGenerator]: undefined, // Costs for free users. See ActionsWithCost.DesignGeneratorFreeUser
  [ActionsWithCost.DesignGeneratorFreeUser]: 1,
  [ActionsWithCost.BackgroundRemoverWebsite]: 1, // Only for free users. Paid users get it for free
  [ActionsWithCost.ImageReplacing]: 1,
  [ActionsWithCost.TextGenerator]: 1,
  [ActionsWithCost.SetsGenerator]: 1, // Times the number of sets
  [ActionsWithCost.StyleSwitch]: 1,
  [ActionsWithCost.Outpainting]: 2,
};

// Returns the credit cost of the action. Undefined if the action is free
export const getOperationCreditCost = (
  action: ActionsWithCost,
  imageGenModel?: ImageGenerationModel
): number | undefined => {
  if (action === ActionsWithCost.ImageGeneration) {
    return creditsCostUtil[action][imageGenModel ?? ImageGenerationModel.Turbo];
  }
  return creditsCostUtil[action];
};
